import { AxiosResponse } from 'axios'
import { UseMutationOptions, UseMutationResult, useMutation } from 'react-query'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { useFetch } from 'providers/FetchProvider'
import { Label } from 'types/Label'
import { AddLabelVariables, EditLabelVariables } from 'api/mutations/labels/types'

const useAddLabelMutation = (
  options: UseMutationOptions<Label, Error, AddLabelVariables>,
  companyId?: number
): UseMutationResult<Label, Error, AddLabelVariables> => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { fetch } = useFetch()

  let url = '/label'
  if (companyId !== undefined) {
    url = `/label/company/${companyId}`
  }

  const { onSuccess } = options || {}

  return useMutation(
    (label: AddLabelVariables) => (
      fetch.post(url, label).then((response) => response.data)
    ),
    {
      ...options,
      onSuccess: (data, ...rest) => {
        onSuccess?.(data, ...rest)
        enqueueSnackbar(
          t('labels.action.success.add', { name: data.name }),
          { variant: 'success' }
        )
      },
      onError: (_, { name }) => {
        enqueueSnackbar(t('labels.action.error.add', { name }), { variant: 'error' })
      },
    },
  )
}

const useEditLabelMutation = (
  options: UseMutationOptions<Label, Error, EditLabelVariables>
): UseMutationResult<Label, Error, EditLabelVariables> => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { fetch } = useFetch()

  const { onSuccess } = options || {}

  return useMutation(
    ({ id, ...label }: EditLabelVariables) => (
      fetch.put(`/label/${id}`, label).then((response) => response.data)
    ),
    {
      ...options,
      onSuccess: (data, ...rest) => {
        onSuccess?.(data, ...rest)
        enqueueSnackbar(
          t('labels.action.success.edit', { name: data.name }),
          { variant: 'success' }
        )
      },
      onError: () => {
        enqueueSnackbar(t('labels.action.error.edit'), { variant: 'error' })
      },
    },
  )
}

const useDeleteLabel = (
  options: UseMutationOptions<AxiosResponse<unknown>, Error, number>
) : UseMutationResult<AxiosResponse<unknown>, Error, number> => {
  const { fetch } = useFetch()

  return useMutation((id) => fetch.delete(`/label/${id}`), options)
}

export {
  useAddLabelMutation,
  useEditLabelMutation,
  useDeleteLabel,
}
